/**
 * The setup panel: scenario choice, and every parameter in the model.
 *
 * Organised so the two audiences both get what they came for. Someone who wants to press
 * play and watch gets the scenario list and a handful of movement-side controls at the
 * top. Someone adapting the model to a case they are studying can open any sector, any
 * institution, and the model's own internal rates, and change anything.
 */

import { useState } from 'react';
import {
  MEDIA_PARAMETERS,
  MOVEMENT_PARAMETERS,
  PILLAR_PARAMETERS,
  REGIME_PARAMETERS,
  SECTOR_PARAMETERS,
  TUNABLE_PARAMETERS,
  DEFAULT_TUNABLES,
  readParameter,
  type ParameterMeta,
  type Scenario,
  type Tunables,
} from 'walkout-engine';
import type { ConfigState } from '../state/useConfig.ts';
import { SCENARIOS } from '../state/scenarios.ts';
import { PROJECT_URL } from '../share/links.ts';
import { ParameterControl } from './ParameterControl.tsx';
import './SetupPanel.css';

type Tab = 'scenario' | 'sectors' | 'pillars' | 'model';

function Disclosure({
  title,
  subtitle,
  children,
  defaultOpen = false,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className={`disclosure${open ? ' disclosure--open' : ''}`}>
      <button type="button" className="disclosure__head" onClick={() => setOpen((o) => !o)}>
        <span className="disclosure__marker" aria-hidden="true">
          {open ? '−' : '+'}
        </span>
        <span className="disclosure__title">{title}</span>
        {subtitle && <span className="disclosure__subtitle">{subtitle}</span>}
      </button>
      {open && <div className="disclosure__body">{children}</div>}
    </section>
  );
}

function ScenarioTab({ state }: { state: ConfigState }) {
  const { scenario, config } = state;
  return (
    <>
      <div className="scenario-list">
        {SCENARIOS.map((option: Scenario) => (
          <button
            key={option.id}
            type="button"
            className={`scenario-card${option.id === scenario.id ? ' scenario-card--active' : ''}`}
            onClick={() => state.selectScenario(option)}
          >
            <span className="scenario-card__name">{option.name}</span>
            <span className={`scenario-card__confidence scenario-card__confidence--${option.confidence}`}>
              {option.confidence} confidence
            </span>
          </button>
        ))}
      </div>

      <p className="scenario-premise">{scenario.premise}</p>
      <p className="scenario-demand">
        <span className="label">The demand</span>
        {scenario.demand}
      </p>

      <Disclosure title="Where these numbers came from" subtitle={`${scenario.sources.length} sources`}>
        <p className="provenance">{scenario.provenance}</p>
        <ul className="sources">
          {scenario.sources.map((source) => (
            <li key={source.ref}>
              <strong>{source.title}</strong>
              <span className="sources__ref">{source.ref}</span>
              <span className="sources__supports">{source.supports}</span>
            </li>
          ))}
        </ul>
      </Disclosure>

      <h3 className="group-heading">The movement</h3>
      {MOVEMENT_PARAMETERS.map((meta) => (
        <ParameterControl
          key={meta.path}
          meta={meta}
          value={readParameter(config.movement, meta.path) ?? 0}
          baseline={readParameter(scenario.movement, meta.path) ?? 0}
          onChange={(value) => state.setGroupParameter('movement', meta, value)}
        />
      ))}

      <h3 className="group-heading">The media</h3>
      {MEDIA_PARAMETERS.map((meta) => (
        <ParameterControl
          key={meta.path}
          meta={meta}
          value={readParameter(config.media, meta.path) ?? 0}
          baseline={readParameter(scenario.media, meta.path) ?? 0}
          onChange={(value) => state.setGroupParameter('media', meta, value)}
        />
      ))}

      <h3 className="group-heading">The regime</h3>
      {REGIME_PARAMETERS.map((meta) => (
        <ParameterControl
          key={meta.path}
          meta={meta}
          value={readParameter(config.regime, meta.path) ?? 0}
          baseline={readParameter(scenario.regime, meta.path) ?? 0}
          onChange={(value) => state.setGroupParameter('regime', meta, value)}
        />
      ))}

      <h3 className="group-heading">The run</h3>
      <div className="run-settings">
        <label className="run-settings__field">
          <span className="label">Seed</span>
          <input
            type="number"
            value={state.overrides.seed}
            min={0}
            onChange={(event) => state.setSeed(Number(event.target.value) || 0)}
          />
        </label>
        <label className="run-settings__field">
          <span className="label">Horizon (days)</span>
          <input
            type="number"
            value={config.horizonDays}
            min={1}
            max={3650}
            onChange={(event) => state.setHorizon(Number(event.target.value) || 1)}
          />
        </label>
        <p className="run-settings__note">
          The same seed and the same settings always produce exactly the same run. Change the
          seed to see how much of an outcome was luck.
        </p>
      </div>
    </>
  );
}

function SectorsTab({ state }: { state: ConfigState }) {
  const { scenario, config } = state;
  return (
    <>
      <p className="tab-intro">
        Each sector is a slice of the workforce with its own savings, its own exposure to
        risk, and its own sense of when enough others have moved. Every field is editable —
        substitute your own figures for a case you are studying.
      </p>
      {config.sectors.map((sector) => {
        const baseline = scenario.sectors.find((s) => s.id === sector.id) ?? sector;
        return (
          <Disclosure
            key={sector.id}
            title={sector.name}
            subtitle={`${Math.round(sector.size * 100)}% of workforce · ${Math.round(sector.bufferDays)}d savings`}
          >
            {SECTOR_PARAMETERS.map((meta: ParameterMeta) => (
              <ParameterControl
                key={meta.path}
                meta={meta}
                value={readParameter(sector, meta.path) ?? 0}
                baseline={readParameter(baseline, meta.path) ?? 0}
                onChange={(value) => state.setSectorParameter(sector.id, meta, value)}
              />
            ))}
          </Disclosure>
        );
      })}
    </>
  );
}

function PillarsTab({ state }: { state: ConfigState }) {
  const { scenario, config } = state;
  return (
    <>
      <p className="tab-intro">
        The institutions the regime rests on. Participation matters mainly through what it
        does to these — a campaign wins by making them stop cooperating, not by being large.
        A pillar that defects never comes back.
      </p>
      {config.pillars.map((pillar) => {
        const baseline = scenario.pillars.find((p) => p.id === pillar.id) ?? pillar;
        return (
          <Disclosure
            key={pillar.id}
            title={pillar.name}
            subtitle={`${Math.round(pillar.loyalty * 100)}% loyal · ${Math.round(pillar.weight * 100)}% of state capacity`}
          >
            {pillar.id === 'security-forces' && (
              <p className="pillar-note">
                Special: repression is capped by this pillar's loyalty. Once it defects, the
                order is still given and nothing happens.
              </p>
            )}
            {PILLAR_PARAMETERS.map((meta) => (
              <ParameterControl
                key={meta.path}
                meta={meta}
                value={readParameter(pillar, meta.path) ?? 0}
                baseline={readParameter(baseline, meta.path) ?? 0}
                onChange={(value) => state.setPillarParameter(pillar.id, meta, value)}
              />
            ))}
          </Disclosure>
        );
      })}
    </>
  );
}

function ModelTab({ state }: { state: ConfigState }) {
  const tunables: Tunables = { ...DEFAULT_TUNABLES, ...state.config.tunables };
  return (
    <>
      <p className="tab-intro">
        These are the model's own internal rates. Not one of them has been measured by
        anyone, which is exactly why they are controls rather than numbers buried in the
        source. <strong>The right way to use this model is to move them and check whether
        your conclusion survives.</strong> If it does not, that is the finding.
      </p>
      {TUNABLE_PARAMETERS.map((meta) => (
        <ParameterControl
          key={meta.path}
          meta={meta}
          value={tunables[meta.path as keyof Tunables]}
          baseline={DEFAULT_TUNABLES[meta.path as keyof Tunables]}
          onChange={(value) => state.setTunable(meta.path as keyof Tunables, value)}
        />
      ))}
    </>
  );
}

const TABS: { id: Tab; label: string }[] = [
  { id: 'scenario', label: 'Scenario' },
  { id: 'sectors', label: 'Sectors' },
  { id: 'pillars', label: 'Pillars' },
  { id: 'model', label: 'Model' },
];

export function SetupPanel({ state }: { state: ConfigState }) {
  const [tab, setTab] = useState<Tab>('scenario');

  return (
    <aside className="setup">
      <nav className="setup__tabs">
        {TABS.map((entry) => (
          <button
            key={entry.id}
            type="button"
            className={`setup__tab${tab === entry.id ? ' setup__tab--active' : ''}`}
            onClick={() => setTab(entry.id)}
          >
            {entry.label}
          </button>
        ))}
      </nav>

      {state.editCount > 0 && (
        <div className="setup__edits">
          <span>
            {state.editCount} parameter{state.editCount === 1 ? '' : 's'} changed from{' '}
            {state.scenario.name}
          </span>
          <button type="button" onClick={state.resetEdits}>
            reset all
          </button>
        </div>
      )}

      <div className="setup__body">
        {tab === 'scenario' && <ScenarioTab state={state} />}
        {tab === 'sectors' && <SectorsTab state={state} />}
        {tab === 'pillars' && <PillarsTab state={state} />}
        {tab === 'model' && <ModelTab state={state} />}
      </div>

      {/* Every screen has a route to the documentation and the limitations. A model this
          full of assumptions has no business hiding the page that lists them. */}
      <footer className="setup__foot">
        <a href={`https://${PROJECT_URL}/blob/main/MODEL.md`} target="_blank" rel="noreferrer">
          How this model works
        </a>
        <a
          href={`https://${PROJECT_URL}/blob/main/MODEL.md#limitations`}
          target="_blank"
          rel="noreferrer"
          className="setup__foot-limits"
        >
          What it cannot tell you
        </a>
        <a href={`https://${PROJECT_URL}`} target="_blank" rel="noreferrer">
          Source
        </a>
      </footer>
    </aside>
  );
}
